import { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock3, MapPin } from 'lucide-react';
import './marketing/owlet.css';

const QUESTIONS = [
  {
    id: 'class',
    label: 'Your next class',
    question: 'What is my next class?',
    title: 'Data Structures',
    detail: 'Today, 2:00–3:30 PM',
    location: 'Block C · Room 204',
    category: 'On your timetable',
  },
  {
    id: 'bus',
    label: 'Your way home',
    question: 'Where can I catch the campus bus?',
    title: 'Campus Route 3',
    detail: 'Today, 2:10 PM',
    location: 'Main campus · Gate 2',
    category: 'Your campus connection',
  },
  {
    id: 'exam',
    label: 'Your next exam',
    question: 'When is my next exam?',
    title: 'Database Systems',
    detail: 'Monday, 9:00 AM',
    location: 'Main campus · Exam Hall A',
    category: 'Coming up next',
  },
] as const;

export function OwletSection() {
  const [activeQuestion, setActiveQuestion] = useState(0);
  const reduceMotion = useReducedMotion();
  const orbRef = useRef<HTMLDivElement>(null);
  const orbInView = useInView(orbRef, { margin: '100px' });
  const sample = QUESTIONS[activeQuestion];

  return (
    <section className="mk-owlet mk-section" aria-labelledby="mk-owlet-title">
      <div className="mk-container mk-owlet-layout">
        <motion.div
          className="mk-owlet-editorial"
          initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: reduceMotion ? 0 : 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mk-eyebrow mk-owlet-eyebrow">02 / MEET OWLET AI</p>
          <h2 className="mk-heading mk-owlet-heading" id="mk-owlet-title">
            Your campus.<br /><span>Just ask.</span>
          </h2>
          <p className="mk-copy mk-owlet-copy">
            Classes, exams, and getting around. A little campus wisdom,
            whenever you need it.
          </p>
          <Link to="/student-form" className="mk-text-link mk-owlet-cta">
            Meet your companion <ArrowUpRight size={19} aria-hidden="true" />
          </Link>

          <div className="mk-owlet-prompts" role="group" aria-label="Choose a sample question">
            {QUESTIONS.map((question, index) => (
              <button
                key={question.id}
                type="button"
                className="mk-owlet-question-option"
                aria-pressed={activeQuestion === index}
                onClick={() => setActiveQuestion(index)}
              >
                <span className="mk-owlet-prompt-number">0{index + 1}</span>
                <span>{question.label}</span>
                <ArrowUpRight size={18} aria-hidden="true" />
                {activeQuestion === index && (
                  <motion.span
                    className="mk-owlet-prompt-line"
                    layoutId="owlet-selected-question"
                    transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 34 }}
                  />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="mk-owlet-experience">
          <div ref={orbRef} className="mk-owlet-orb" aria-hidden="true">
            <span className="mk-owlet-orb-halo" />
            <svg className="mk-owlet-orb-axis" viewBox="0 0 360 260" fill="none">
              <path d="M0 130H360M180 0V260" stroke="currentColor" strokeDasharray="2 7" />
              <path d="M0 125V135M360 125V135M175 0H185M175 260H185" stroke="currentColor" />
            </svg>
            <motion.svg
              className="mk-owlet-orb-sphere"
              viewBox="0 0 240 240"
              fill="none"
              animate={reduceMotion || !orbInView ? { rotate: 0 } : { rotate: 360 }}
              transition={reduceMotion || !orbInView ? { duration: 0 } : { duration: 60, repeat: Infinity, ease: 'linear' }}
            >
              <defs>
                <radialGradient id="owlet-orb-glow" cx="34%" cy="27%" r="78%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity=".8" />
                  <stop offset="50%" stopColor="var(--mk-brand-pale)" stopOpacity=".2" />
                  <stop offset="100%" stopColor="var(--mk-brand)" stopOpacity=".22" />
                </radialGradient>
              </defs>
              <circle cx="120" cy="120" r="98" fill="url(#owlet-orb-glow)" />
              <g stroke="currentColor" strokeWidth=".7">
                <circle cx="120" cy="120" r="98" />
                <ellipse cx="120" cy="120" rx="98" ry="36" />
                <ellipse cx="120" cy="120" rx="98" ry="70" />
                <ellipse cx="120" cy="120" rx="36" ry="98" />
                <ellipse cx="120" cy="120" rx="70" ry="98" />
                <ellipse cx="120" cy="120" rx="36" ry="98" transform="rotate(45 120 120)" />
                <ellipse cx="120" cy="120" rx="36" ry="98" transform="rotate(-45 120 120)" />
              </g>
              <circle cx="120" cy="22" r="4" fill="var(--mk-brand-dark)" />
              <circle cx="218" cy="120" r="2.5" fill="var(--mk-brand-dark)" />
            </motion.svg>
            <span className="mk-owlet-orb-center"><span /></span>
          </div>

          <div className="mk-owlet-conversation" aria-live="polite" aria-atomic="true">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={sample.id}
                className="mk-owlet-answer"
                initial={{ opacity: 0, y: reduceMotion ? 0 : 15, clipPath: reduceMotion ? 'none' : 'inset(0 0 8% 0)' }}
                animate={{ opacity: 1, y: 0, clipPath: reduceMotion ? 'none' : 'inset(0 0 0% 0)' }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
                transition={{ duration: reduceMotion ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="mk-owlet-question">{sample.question}</p>
                <p className="mk-owlet-answer-category">{sample.category}</p>
                <h3>{sample.title}</h3>
                <div className="mk-owlet-detail-meta">
                  <span><Clock3 size={15} aria-hidden="true" />{sample.detail}</span>
                  <span><MapPin size={15} aria-hidden="true" />{sample.location}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <p className="mk-owlet-disclaimer">Sample conversation · Personalised to your profile when you sign in.</p>
        </div>
      </div>
    </section>
  );
}

export default OwletSection;
